import { addAddressPadding } from 'starknet';
import { ADDRESS_CHAR_LENGTH, BACKEND_TOKEN_MAP } from '../constants';
import { DEFAULT_DECIMAL_PRECISION } from '../datastructures';
import { AssetConfig, Config, NostraToken, PoolPair, PoolToken, Token } from '../interfaces';
import { BackendToken } from '../interfaces/Token';

class ConfigManager {
  private config: Config;

  private tokenList: Token[] = [];
  private tokenAddresses: string[] = [];
  private nostraTokenList: NostraToken[] = [];
  private nostraTokenToAssetMap: Map<string, string> = new Map();

  constructor(config: Config) {
    this.config = config;

    this.retrieveTokenList();
    this.retrieveTokenAddresses();
    this.retrieveNostraTokenList();
    this.retrieveNostraTokenToAssetMap();
  }

  getConfig(): Config {
    return this.config;
  }

  getTokenList(): Token[] {
    return this.tokenList;
  }

  getBaseAsset(): AssetConfig {
    return this.config.assets['ETH'];
  }

  getTokenAddresses(): string[] {
    return this.tokenAddresses;
  }

  getNostraTokenList(): NostraToken[] {
    return this.nostraTokenList;
  }

  getPoolPairConfig(tokenATicker: PoolToken, tokenBTicker: PoolToken): PoolPair {
    // First try direct key lookup
    const directMatch = this.config.pools[`${tokenATicker}-${tokenBTicker}`];
    if (directMatch) {
      return directMatch.pair;
    }

    // Fall back to searching by token tickers (handles cases where pool key
    // doesn't match tickers, e.g., key is 'USDC-USDT' but ticker is 'USDC.e')
    const entry = Object.values(this.config.pools).find(
      pool => pool.pair.tokenA.ticker === tokenATicker && pool.pair.tokenB.ticker === tokenBTicker,
    );
    if (entry) {
      return entry.pair;
    }

    throw new Error(`Pool pair config not found for ${tokenATicker}-${tokenBTicker}`);
  }

  getAssetByAddress(tokenAddress: string): AssetConfig | null {
    const entry = Object.entries(this.config.assets).find(
      ([, asset]) =>
        asset.address === tokenAddress ||
        asset.nostraInterestBearingTokenAddress === tokenAddress ||
        asset.nostraInterestBearingCollateralTokenAddress === tokenAddress ||
        asset.nostraTokenAddress === tokenAddress ||
        asset.nostraCollateralTokenAddress === tokenAddress ||
        asset.debtTokenAddress === tokenAddress,
    );
    return entry ? entry[1] : null;
  }

  getAssetByTicker(assetTicker: Token): AssetConfig {
    return this.config.assets[assetTicker];
  }

  getAssetByBackendToken(backendToken: BackendToken): AssetConfig {
    const token: Token = BACKEND_TOKEN_MAP[backendToken];
    return this.config.assets[token];
  }

  getTokenByAddress(tokenAddress: string): Token | null {
    return this.getAssetByAddress(tokenAddress)?.ticker ?? null;
  }

  getTokenPrecision(tokenOrAddress: Token | string): number {
    const token =
      tokenOrAddress.length === ADDRESS_CHAR_LENGTH ? this.getTokenByAddress(tokenOrAddress) : tokenOrAddress;
    return token
      ? this.config.assets[token as Token]?.tokenPrecision ?? DEFAULT_DECIMAL_PRECISION
      : DEFAULT_DECIMAL_PRECISION;
  }

  getNostraTokenAssetAddress(nostraTokenAddress: string): string {
    const assetAddress = this.nostraTokenToAssetMap.get(addAddressPadding(nostraTokenAddress));

    if (!assetAddress) {
      throw new Error(`Failed to get asset address for nostra token ${nostraTokenAddress}!`);
    }

    return assetAddress;
  }

  private retrieveNostraTokenToAssetMap(): void {
    Object.entries(this.config.assets).forEach(([, asset]) => {
      this.nostraTokenToAssetMap.set(
        addAddressPadding(asset.nostraInterestBearingCollateralTokenAddress),
        asset.address,
      );
      this.nostraTokenToAssetMap.set(addAddressPadding(asset.nostraInterestBearingTokenAddress), asset.address);
      this.nostraTokenToAssetMap.set(addAddressPadding(asset.nostraCollateralTokenAddress), asset.address);
      this.nostraTokenToAssetMap.set(addAddressPadding(asset.nostraTokenAddress), asset.address);
      this.nostraTokenToAssetMap.set(addAddressPadding(asset.debtTokenAddress), asset.address);
    });
  }

  private retrieveNostraTokenList(): void {
    this.nostraTokenList = Object.entries(this.config.assets).flatMap(
      ([, asset]) =>
        [
          {
            address: asset.nostraInterestBearingTokenAddress,
            variant: {
              interestBearing: true,
              collateral: false,
            },
            symbol: asset.nostraInterestBearingTokenSymbol,
            underlyingToken: asset.ticker,
            tokenPrecision: asset.tokenPrecision,
            assetAddress: asset.address,
          },
          {
            address: asset.nostraInterestBearingCollateralTokenAddress,
            variant: {
              interestBearing: true,
              collateral: true,
            },
            symbol: asset.nostraInterestBearingCollateralTokenSymbol,
            underlyingToken: asset.ticker,
            tokenPrecision: asset.tokenPrecision,
            assetAddress: asset.address,
          },
          {
            address: asset.nostraTokenAddress,
            variant: {
              interestBearing: false,
              collateral: false,
            },
            symbol: asset.nostraTokenSymbol,
            underlyingToken: asset.ticker,
            tokenPrecision: asset.tokenPrecision,
            assetAddress: asset.address,
          },
          {
            address: asset.nostraCollateralTokenAddress,
            variant: {
              interestBearing: false,
              collateral: true,
            },
            symbol: asset.nostraCollateralTokenSymbol,
            underlyingToken: asset.ticker,
            tokenPrecision: asset.tokenPrecision,
            assetAddress: asset.address,
          },
          {
            address: asset.debtTokenAddress,
            variant: 'debt',
            symbol: asset.debtTokenSymbol,
            underlyingToken: asset.ticker,
            tokenPrecision: asset.tokenPrecision,
            assetAddress: asset.address,
          },
        ] as NostraToken[],
    );
  }

  private retrieveTokenList(): void {
    this.tokenList = Object.keys(this.config.assets)
      .map(token => token as Token)
      .sort();
  }

  private retrieveTokenAddresses(): void {
    this.tokenAddresses = Object.keys(this.config.assets).map(token => this.config.assets[token as Token].address);
  }

  getPoolTokenAddressFromTicker(ticker: string): string {
    let result = '';

    Object.entries(this.config.pools).forEach(([, value]) => {
      if (value.pair.tokenA.ticker === ticker) {
        result = value.pair.tokenA.address;
      }
      if (value.pair.tokenB.ticker === ticker) {
        result = value.pair.tokenB.address;
      }
    });

    return result;
  }

  getPoolTokenTickerFromAddress(address: string): PoolToken | null {
    let result: PoolToken | null = null;

    Object.entries(this.config.pools).forEach(([, value]) => {
      if (value.pair.tokenA.address === address) {
        result = value.pair.tokenA.ticker;
      }
      if (value.pair.tokenB.address === address) {
        result = value.pair.tokenB.ticker;
      }
    });

    return result;
  }
}

export default ConfigManager;
